/**
 * Call Us page redesign
 * Rendering Call us information with `More Help options`, which is replacing `find more answers` UI style
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import { bindActionCreators } from 'redux';

// Helper
import i18n from '../../lib/reacti18n';
import Page from '../../lib/page';
import { chatBotContactTo } from '../constants/UtilVariables';
import { redirectLocation } from '../actions/action-contact-location';
import { backToHome } from '../actions/action-call-us';
import { postChatBotEligibility } from '../actions/action-postchatbot-eligibility';
import { trackLink, trackPage } from '../../lib/analytics-helper';
import parserContactUs from '../../lib/dataParser';
import {
  CALL_US_PHONE_INFO,
  CONTACT_OPTION,
  SELECT_RESULT,
  TOPIC_TREE_SELECT_RESULT,
  TOPICSELECT,
  WEBFORM_FIELDS_DETAILS,
} from '../constants/PropTypesShapes';

import Dialing from '../components/Dialing';
import DialTabs from '../components/common/callUsElmoCommon/DialTabs';
import NavBackArrow from '../components/NavBackArrow';
import { validateAMFlag } from '../../lib/globalHelper';
import { selectTopicReset } from '../actions/action-select-topic';

// constants/Label
const label = {
  dialingHeader: i18n.message('smarthelp/callUs.call.dialing.header'),
  logoutInst: i18n.message('smarthelp/callUs.call.dialing.customer.logout.inst'),
  dialingSubHeader: i18n.message('smarthelp/callUs.call.dialing.customer.subInst.header'),
  dialingMobileSubHeader: i18n.message('smarthelp/callUs.call.dialing.mobile.tabInst'),
  dialingSubBody: i18n.message('smarthelp/callUs.call.dialing.customer.subInst.body'),
  additionInst: i18n.message('smarthelp/callUs.callUs.splInstruction'),
  serviceHourHeader: i18n.message('smarthelp/callUs.call.dialing.service.hour.headr'),
  subHoursHeader: i18n.message('smarthelp/callUs.call.dialing.service.hour.subHeadr'),
  paypal: i18n.message('components/component-header-footer/header/headerNoLogin.header.brand'),
  PPCHeader: i18n.message('smarthelp/callUs.call.dialing.customer.subInst.ppcHeader'),
  PPCredit: i18n.message('smarthelp/callUs.callUsPanel.PPC.topic'),
  PPCtopicChecker: i18n.message('smarthelp/callUs.callUsPanel.PPC.topic'),
  mobileLogoutInst: i18n.message('smarthelp/callUs.call.dialing.mobile.logout.inst'),
  dialingAlterSubHeader: i18n.message('smarthelp/callUs.call.dialing.customer.subInst.alterHeader'),
  dialingMobileAlterSubHeader: i18n.message('smarthelp/callUs.call.dialing.mobile.alterTabInst'),
  mobileCallUsHeader: i18n.message('smarthelp/callUs.call.dialing.mobile.callHeader'),
  mobileCallUsHeaderWithWebPin: i18n.message('smarthelp/callUs.call.dialing.mobile.callHeaderWithWebPin'),
};

const PPCardTopicRef = 'paypal_cards_l1';
const PP_CREDIT = 'PP_CREDIT';
const PP_CREDIT_HOURS_OF_OPERATIONS = 'PP_CREDIT_HOURS_OF_OPERATIONS';

class Call extends Component {
  // FPTI event for clicking/selecting tab between 'PayPal' and 'PayPal Credit'
  static tabFPTIHandler(selection = '') {
    return trackLink({ id: 'callPage', link: `select-default-number|${selection}` });
  }

  /**
   * Function to return pp credit values from SAC numbers
   * @param ppSacNumbers
   * @param creditLabel
   * @returns {null|*}
   */
  static getPPCreditValue(ppSacNumbers, creditLabel) {
    if (Array.isArray(ppSacNumbers) && ppSacNumbers.length) {
      try {
        return ppSacNumbers.find((item) => item.phone_type === creditLabel).phone_value;
      } catch (e) {
        return null;
      }
    }

    return null;
  }

  // Check whether the webpin exist or not
  static hasWebPin(contactInfo) {
    return contactInfo && !!contactInfo.webPin;
  }

  constructor(props) {
    super(props);
    this.clickBackToHome = this.clickBackToHome.bind(this);
  }

  componentDidMount() {
    // Added here as this FPTI is re-rendering in app.js file
    trackPage({ id: 'callPage', link: 'smarthelp-call' });

    const { contactInfo } = this.props;
    // Send FPTI impression event for login webpin and for non-idi markets
    // as we need perform AM flag validation by calling callus API before landing into callus page
    if (validateAMFlag()) {
      if (contactInfo && contactInfo.web_pin) {
        trackPage({ id: 'webPinSuccess', link: 'smarthelp-call-us|webpin_displayed' });
      } else if (contactInfo && contactInfo.isphonePresent) {
        trackPage({ id: 'webPinNotApplicable', link: 'smarthelp-call-us|webpin_not_applicable' });
      } else {
        trackPage({ id: 'webPinfailure', link: 'smarthelp-call-us|webpin_not_displayed' });
      }
    }
  }

  // Check whether the first layer 1 topic is `PayPal Credit` or not
  isTopicPPCredit() {
    return (
      this.props.topicSelect &&
      this.props.topicSelect.topics &&
      this.props.topicSelect.topics.layer1 === label.PPCtopicChecker
    );
  }

  // Check whether the first layer 1 topic is `PayPal Card` or not
  isPayPalCardTopic() {
    return (
      this.props.topicSelect &&
      this.props.topicSelect.topicRef &&
      this.props.topicSelect.topicRef.layer1 === PPCardTopicRef
    );
  }

  // User reach to the topic tree leaf node and get Select result
  isTopicLeafNode() {
    return !_isEmpty(this.props.selectResult.topicTreeSelectResult);
  }

  isFinalIntent() {
    // check topicTreeSelectResult existance for final intent
    const { smcResultKeeper } = this.props;
    return !_isEmpty(smcResultKeeper.topicTreeSelectResult);
  }

  // showing tab for Logout users or PPC enabled users
  isDisplayPPCredit() {
    return Page.isLogoutUS() || this.props.PPCEnable;
  }

  /**
   * isShowTab: Render PayPayCredit and PayPal phone nuber and customer hour switching by tab
   * PPCredit display logic:
   * Not react to end of topic tree leaf node with two cases:
   *  1). for US logout users
   *  2). for Login account, the account is PP credit enable acccount
   */
  isShowTab() {
    // Checking PayPal credit number exist or not from the sac result
    if (!this.isPPCreditInfoExists()) {
      return false;
    }
    return !this.isTopicLeafNode() && this.isDisplayPPCredit();
  }

  /**
   * Method used to verify the sac phone numbers result exists and the verifying PP_CREDIT and PP_CREDIT_HOURS_OF_OPERATIONS key exists.
   * @returns {null|boolean}
   */

  isPPCreditInfoExists() {
    const sacContactInfo = this.props.contactInfo;
    if (!sacContactInfo || !Array.isArray(sacContactInfo.ppSACNumbers) || !sacContactInfo.ppSACNumbers.length) {
      return false;
    }
    const ppSACNumbers = sacContactInfo.ppSACNumbers;
    const ppCreditPhNumber = Call.getPPCreditValue(ppSACNumbers, PP_CREDIT);
    const ppCreditHoursOfOperation = Call.getPPCreditValue(ppSACNumbers, PP_CREDIT_HOURS_OF_OPERATIONS);
    return !_isEmpty(ppCreditPhNumber) && !_isEmpty(ppCreditHoursOfOperation);
  }

  // making chatbot eligibility API call when user lands on channel-steering home page from callus page
  chatBotEligibiltyAPI() {
    const { selectResult } = this.props;
    let postChatBotResponse = '';
    if (!_isEmpty(selectResult.topicTreeSelectResult)) {
      // for final Intent Page
      postChatBotResponse = Page.getChatBotEligibilityParams(selectResult.topicTreeSelectResult.topicRefId, 'TOPIC');
    } else {
      // for default page
      postChatBotResponse = Page.getChatBotEligibilityParams('contactUSpage', 'GENERIC');
    }
    return this.props.postChatBotEligibility(postChatBotResponse);
  }

  clickBackToHome() {
    const { selectResult, smcResultKeeper } = this.props;
    trackLink({ id: 'callPage', link: 'channel-dialing-back' });

    // During logout to login scenario, when you click back on callus, it should land on default page and
    // resetting smc-result keeper state to see valid phone number in callus in contact-us default page
    // instead of seeing the same number as final intent
    if (_isEmpty(selectResult.topicTreeSelectResult) && !_isEmpty(smcResultKeeper.topicTreeSelectResult)) {
      // for logout to login final intent callus
      this.props.selectTopicReset();
    }
    if (Page.getChatBotContactTo() === chatBotContactTo.call) {
      window.open('/smarthelp/contact-us', '_self');
    } else {
      // reset callus response to users other than loggedin and non idi markets
      if (!validateAMFlag()) {
        this.props.backToHome();
      }
      this.props.redirectLocation({ pageName: 'contactUs' });
      this.chatBotEligibiltyAPI();
    }
  }

  // Data filter of Dialing Tab component
  tabContentsFilter() {
    return [
      {
        label: label.paypal,
        logo: 'contact-dialing-paypal-logo',
        content: this.renderDialingInfo(),
        priority: 1,
        onClick: () => Call.tabFPTIHandler(label.paypal),
      },
      {
        label: label.PPCredit,
        logo: 'contact-dialing-ppcredit-logo',
        content: this.renderPPCInfo(this.props.contactInfo),
        priority: 2,
        onClick: () => Call.tabFPTIHandler(label.PPCredit),
      },
    ];
  }

  // Render the Customer Phone number which got from SAC
  renderDialingInfo() {
    // Passing contactinfo, form fields and final intent condition for data parsing Contact Us data
    const parsedInfo = parserContactUs(
      this.props.contactInfo,
      this.props.formFields,
      this.isFinalIntent(),
      this.props.chatParams,
    );
    let header = Call.hasWebPin(parsedInfo) ? label.dialingAlterSubHeader : label.dialingSubHeader;
    if (this.isTopicPPCredit()) {
      header = label.PPCHeader;
    }
    const logoutInst = this.isTopicPPCredit() || this.isPayPalCardTopic() ? label.mobileLogoutInst : label.logoutInst;
    const mobileHeader = Call.hasWebPin(parsedInfo) ? label.mobileCallUsHeaderWithWebPin : label.mobileCallUsHeader;

    const ppCreditTopics = this.isTopicPPCredit() || this.isPayPalCardTopic();
    // Customize the login instruction and header; The login instruction will not show webpin reference for PPC topics
    const contactInfo = {
      ...parsedInfo,
      header,
      mobileHeader,
      logoutInst,
      mobileLogoutInst: label.mobileLogoutInst,
      ppCreditTopics,
    };
    return <Dialing contactInfo={contactInfo} contactChannels={this.renderContactOptions()} />;
  }

  /**
   * renderPayPalInfo (login case)
   * 1. User has PayPal Credit account and clicks Call us without an intent - sees both PP and PPC number/hours on Callus us page
   * 2. User does not have PayPal Credit account and clicks Call us without an intent - sees only PP number/hours on Callus Us page
   * 3. User selects an intent and then clicks Call Us - show either the PP number or PP Credit number (or whatever is hardcoded for that topic, for PP Working Capital, etc.),
   *  regardless of whether they
   */
  renderPayPalInfo() {
    if (this.isShowTab() && _isEmpty(this.props.chatParams)) {
      return this.renderDialingTab();
    }
    // render normal Customer Service hour and Hardcode PPCredit phone data
    return this.renderDialingInfo();
  }

  // Render back button to navigate to default Contact Us page
  renderBackBtn() {
    return <NavBackArrow onClick={this.clickBackToHome} />;
  }

  /**
   * Check for logout to login scenario and selectresult.topicsDisplay value
   */
  renderContactOptions() {
    const { selectResult, contactOptions } = this.props;
    if (selectResult.topicsDisplay) {
      return Page.getIDIContactOptionDecisions();
    }
    return contactOptions;
  }

  // render PayPal credit information
  renderPPCInfo(sacContactInfo) {
    // PayPal credit data parser
    const ppSacNumbers = sacContactInfo.ppSACNumbers;
    const ppCreditPhNumber = Call.getPPCreditValue(ppSacNumbers, PP_CREDIT);
    const ppCreditHoursOfOperation = Call.getPPCreditValue(ppSacNumbers, PP_CREDIT_HOURS_OF_OPERATIONS);
    const contactInfo = {
      header: label.PPCHeader,
      mobileHeader: label.dialingMobileSubHeader,
      logoutInst: label.mobileLogoutInst,
      mobileLogoutInst: label.mobileLogoutInst,
      phoneNumber: [ppCreditPhNumber],
      customerHour: {
        hoursOfService: ppCreditHoursOfOperation,
      },
      ppCreditTopics: true,
    };

    return <Dialing contactInfo={contactInfo} contactChannels={this.renderContactOptions()} />;
  }

  // Render the dialing tab and passing filter results into its 'options' attribute
  renderDialingTab() {
    return <DialTabs options={this.tabContentsFilter()} />;
  }

  render() {
    if (!this.props.contactInfo) {
      return null;
    }
    return (
      <div className="channel-new-call-us-container">
        <div className="col-sm-12 col-md-12 col-lg-12 channel-new-call-header-container">
          {this.renderBackBtn()}
          <h1 className="channel-new-call-us-main-header">{label.dialingHeader}</h1>
        </div>
        <div className="channel-new-call-us-main-content">{this.renderPayPalInfo()}</div>
      </div>
    );
  }
}

Call.propTypes = {
  contactInfo: PropTypes.shape(CALL_US_PHONE_INFO).isRequired,
  redirectLocation: PropTypes.func.isRequired,
  backToHome: PropTypes.func.isRequired,
  PPCEnable: PropTypes.bool.isRequired,
  selectTopicReset: PropTypes.func.isRequired,
  selectResult: PropTypes.shape(TOPIC_TREE_SELECT_RESULT).isRequired,
  topicSelect: PropTypes.shape(TOPICSELECT).isRequired,
  formFields: PropTypes.arrayOf(PropTypes.shape(WEBFORM_FIELDS_DETAILS)).isRequired,
  smcResultKeeper: PropTypes.shape(SELECT_RESULT).isRequired,
  postChatBotEligibility: PropTypes.func.isRequired,
  chatParams: PropTypes.shape({}).isRequired,
  contactOptions: PropTypes.arrayOf(PropTypes.shape(CONTACT_OPTION)).isRequired,
};

function mapStateToProps(state) {
  return {
    contactInfo: state.callUs.callUsResult && state.callUs.callUsResult.contactInfo,
    PPCEnable: state.PPCredit.ppcEnable,
    selectResult: state.selectResult,
    topicSelect: state.topicSelect,
    formFields: state.persistEmailForm.fields,
    smcResultKeeper: state.smcResultKeeper,
    callusLoggedIn: state.callUs.isLogoutToLogin,
    chatParams: state.callUs.chatParams,
    contactOptions: state.contactChannels.IDIAvailability,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      redirectLocation,
      backToHome,
      postChatBotEligibility,
      selectTopicReset,
    },
    dispatch,
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(Call);
